import { useCallback, useMemo, useState } from "react";

import type { Organization } from "@/modules/dashboard/cards/types/organizations-api.types";
import { ColocaCombobox, type ColocaComboboxOption } from "@/shared/components/forms/ColocaCombobox";
import { cn } from "@/shared/lib/utils";

import type { UseOrganizationsCatalogResult } from "../hooks/useOrganizationsCatalog";
import { ORGANIZATION_SEARCH_MIN_LENGTH, useOrganizationSearch } from "../hooks/useOrganizationSearch";

/**
 * The global scope is the ABSENCE of a selection, so the input stays empty for it and typing a search starts
 * from an empty field. This sentinel exists only so the dropdown can offer an explicit way back — the
 * combobox maps `""` to "nothing selected", so a listed item needs a real value.
 */
const GLOBAL_SCOPE = "__global__";

export interface OrganizationScopePickerLabels {
  label: string;
  placeholder: string;
  loading: string;
  empty: string;
  searching: string;
  searchError: string;
  catalogError: string;
  catalogTruncated: string;
  /** Label of the dropdown entry that returns to the global scope. Unused in `required` mode. */
  globalOption?: string;
  /** Shown under the field while no organization is selected, in `scope` mode. */
  globalHint?: string;
  /** Shown under the field while no organization is selected, in `required` mode. */
  requiredHint?: string;
  /** Shown under the field while an organization is selected. */
  orgHint: string;
}

export interface OrganizationScopePickerProps {
  id: string;
  /** `null` selects the global scope — or, in `required` mode, nothing yet. */
  value: string | null;
  /** `label` is what the picked option showed, for callers that list picks outside the catalog page. */
  onValueChange: (organizationId: string | null, label?: string) => void;
  catalog: UseOrganizationsCatalogResult;
  labels: OrganizationScopePickerLabels;
  /**
   * `scope` (default) offers the global scope as the empty field and as a dropdown entry. `required` is for
   * forms that act on behalf of exactly one organization: no global entry, and an empty field means "not
   * chosen yet", which the caller's validation reports.
   */
  mode?: "scope" | "required";
  disabled?: boolean;
  className?: string;
}

function toOption(organization: Organization): ColocaComboboxOption {
  return {
    value: organization.id,
    label: organization.email ? `${organization.name} · ${organization.email}` : organization.name,
  };
}

/**
 * Scope selector shared by the payments-config surfaces that read "global, or this organization": the rail
 * kill-switch and the price book.
 *
 * It exists as one component because the combination it wires is easy to get wrong and was already wrong once
 * in production. Three things have to hold together:
 *
 * 1. **The search is the server's.** `useOrganizationsCatalog` loads one capped page; an organization outside it
 *    — or a pasted uuid — is unreachable by filtering what is already loaded, so past the backend's minimum
 *    term the options ARE the server's answer and `filter={null}` stops the browser narrowing them again.
 *    Below that length there is no request and the preloaded page is filtered here, by id as well as by
 *    label.
 * 2. **The selected option never leaves `options`.** It is what the input renders, so a term that no longer
 *    returns it would blank the field mid-selection.
 * 3. **Only a real edit is a query** — enforced inside `ColocaCombobox`, which is why `onSearchChange` is safe
 *    to feed straight back into `options`.
 *
 * The global scope is deliberately NOT a value in the input. Parking a label there means a click puts the
 * caret after it and the first thing typed becomes part of the query, so the common case — open the page,
 * type an organization — would search for "Global (…)whatever". Empty field IS global; the hint under the
 * field says so, and the input's clear button reads as "back to global".
 */
export function OrganizationScopePicker({
  id,
  value,
  onValueChange,
  catalog,
  labels,
  mode = "scope",
  disabled = false,
  className,
}: OrganizationScopePickerProps) {
  const [query, setQuery] = useState("");
  const [lastPicked, setLastPicked] = useState<ColocaComboboxOption | null>(null);
  const search = useOrganizationSearch(query);
  const isServerSearch = search.activeSearch.length >= ORGANIZATION_SEARCH_MIN_LENGTH;

  const knownById = useMemo(() => {
    const map = new Map<string, ColocaComboboxOption>();
    for (const organization of catalog.organizations) map.set(organization.id, toOption(organization));
    for (const organization of search.results) map.set(organization.id, toOption(organization));
    return map;
  }, [catalog.organizations, search.results]);

  // Whichever source still knows the name wins; the label captured at selection time is the fallback, and the
  // raw id the last resort — an organization arriving from the URL may be in neither source.
  const selectedOption = useMemo<ColocaComboboxOption | null>(() => {
    if (value == null) return null;
    return knownById.get(value) ?? (lastPicked?.value === value ? lastPicked : { value, label: value });
  }, [value, knownById, lastPicked]);

  const options = useMemo(() => {
    const source = isServerSearch ? search.results : catalog.organizations;
    const list = source.map(toOption);
    if (selectedOption && !list.some((option) => option.value === selectedOption.value)) {
      list.unshift(selectedOption);
    }
    // The global entry is a client-side sentinel the server knows nothing about, so while the server owns the
    // filtering this decides for itself whether the term still reaches it.
    const globalOption = mode === "scope" ? labels.globalOption : undefined;
    const globalMatches =
      globalOption !== undefined &&
      (!isServerSearch || globalOption.toLowerCase().includes(search.activeSearch.toLowerCase()));
    return globalMatches ? [{ value: GLOBAL_SCOPE, label: globalOption }, ...list] : list;
  }, [
    isServerSearch,
    search.results,
    search.activeSearch,
    catalog.organizations,
    selectedOption,
    labels,
    mode,
  ]);

  const matchOption = useCallback(
    (optionValue: string, rawQuery: string) => {
      const needle = rawQuery.trim().toLowerCase();
      if (!needle) return true;
      const label = options.find((option) => option.value === optionValue)?.label ?? "";
      return optionValue.toLowerCase().includes(needle) || label.toLowerCase().includes(needle);
    },
    [options],
  );

  const handleChange = useCallback(
    (next: string | null) => {
      if (next == null || next === GLOBAL_SCOPE) {
        setLastPicked(null);
        onValueChange(null);
        return;
      }
      const picked = options.find((option) => option.value === next) ?? { value: next, label: next };
      setLastPicked(picked);
      onValueChange(next, picked.label);
    },
    [options, onValueChange],
  );

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label htmlFor={id} className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        {labels.label}
      </label>
      <ColocaCombobox
        id={id}
        value={value}
        onValueChange={handleChange}
        options={options}
        onSearchChange={setQuery}
        filter={isServerSearch ? null : matchOption}
        renderOption={(option) =>
          option.value === GLOBAL_SCOPE ? (
            option.label
          ) : (
            <span className="flex min-w-0 flex-col">
              <span className="truncate">{option.label}</span>
              <span className="truncate font-mono text-[10px] text-muted-foreground">{option.value}</span>
            </span>
          )
        }
        disabled={disabled || catalog.isLoading}
        placeholder={catalog.isLoading ? labels.loading : labels.placeholder}
        emptyMessage={search.isSearching ? labels.searching : labels.empty}
      />
      <p className="text-xs text-muted-foreground">
        {value
          ? labels.orgHint
          : mode === "required"
            ? (labels.requiredHint ?? labels.placeholder)
            : labels.globalHint}
      </p>
      {/* A catalog that failed or was capped must say so: org names resolve override rows elsewhere. */}
      {(catalog.hasError || catalog.accessDenied) && (
        <p className="text-xs text-warning">{labels.catalogError}</p>
      )}
      {search.hasError && <p className="text-xs text-warning">{labels.searchError}</p>}
      {catalog.isTruncated && <p className="text-xs text-warning">{labels.catalogTruncated}</p>}
    </div>
  );
}
